import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { findSlots, type FindSlotsResponse } from "../api";

// UK postcode patterns:
//   OUTWARD-only (district): "IG5", "N10", "EC1A", "TW7" — 1-2 letters + 1-2
//   digits + optional letter. This is enough to fire the search because the
//   server's HERE lookup table and coverage rules are district-keyed.
//   FULL: outward + optional space + inward (digit + 2 letters). Accepts
//   "TW7 7EQ", "TW77EQ", "EC1A 1BB".
const OUTWARD_RE = /^[A-Z]{1,2}\d{1,2}[A-Z]?$/i;
const FULL_POSTCODE_RE = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}$/i;

/** Frontend mirror of the server's `postcode_district()` — strip non-alphanum,
 *  uppercase, then drop the last 3 chars (inward code) if length > 4. */
export function postcodeDistrict(s: string): string {
  const cleaned = s.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (cleaned.length <= 4) return cleaned;
  return cleaned.slice(0, -3);
}

export function looksLikePostcode(s: string): boolean {
  const trimmed = s.trim();
  if (!trimmed) return false;
  // Strip the optional space when matching outward — covers "IG5", "TW7",
  // "EC1A" plus their padded variants.
  const compact = trimmed.replace(/\s+/g, "");
  return OUTWARD_RE.test(compact) || FULL_POSTCODE_RE.test(trimmed);
}

// Backwards-compat alias for callers that imported the old name.
export const looksLikeFullPostcode = looksLikePostcode;

/**
 * Query-key factory used both by the hook AND by manual invalidation paths.
 * Cached at DISTRICT level — server's HERE table + coverage rules + slot
 * algorithm are all district-keyed, so "TW7" and "TW7 7EQ" produce identical
 * results. Sharing the cache entry means typing the inward code after the
 * outward triggers no extra fetch.
 */
export function slotsQueryKey(args: {
  postcode: string;
  duration: number;
  value: number;
  daysAhead: number;
}): unknown[] {
  return [
    "slots",
    postcodeDistrict(args.postcode),
    args.duration,
    Math.round(args.value * 100) / 100,
    args.daysAhead,
  ];
}

interface UseSlotsArgs {
  postcode: string;
  durationMinutes: number;
  estimatedValueGbp: number;
  jobRef?: string;
  daysAhead?: number;
  /** When false, the query is registered but won't fetch automatically.
   *  Use case: hide the loading state for an inactive form. */
  enabled?: boolean;
  /** Debounce postcode/duration/value churn while the user types. Default 500ms. */
  debounceMs?: number;
}

/**
 * Shared slot-search query. Identical inputs = single in-flight request,
 * 60s of cache reuse, free spinners-go-away when a parent has already
 * prefetched. React Query handles dedup, retries, and stale invalidation.
 *
 * Key behaviours:
 *  - Disabled until postcode looks like a full UK postcode (regex gate).
 *  - Inputs debounced so character-by-character typing doesn't fire 7 calls.
 *  - 60s staleTime matches the FastAPI in-memory cache TTL — repeat hits
 *    inside that window touch zero compute end-to-end.
 *  - Returns `data: undefined` while loading; consumers render a skeleton
 *    or wait for `isFetched`.
 */
export function useSlotsQuery(args: UseSlotsArgs) {
  const debounceMs = args.debounceMs ?? 500;

  // Debounce the inputs that actually change shape with each keystroke.
  const [debounced, setDebounced] = useState({
    postcode: args.postcode,
    duration: args.durationMinutes,
    value: args.estimatedValueGbp,
  });
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced({
        postcode: args.postcode,
        duration: args.durationMinutes,
        value: args.estimatedValueGbp,
      });
    }, debounceMs);
    return () => clearTimeout(t);
  }, [args.postcode, args.durationMinutes, args.estimatedValueGbp, debounceMs]);

  // 60-day look-ahead so customers booking a month or two out get real
  // results. Backend cap raised to le=90 in 2026-05 alongside this change.
  const daysAhead = args.daysAhead ?? 60;
  const enabled =
    (args.enabled ?? true) &&
    looksLikePostcode(debounced.postcode) &&
    debounced.duration > 0;

  const queryKey = slotsQueryKey({
    postcode: debounced.postcode,
    duration: debounced.duration,
    value: debounced.value,
    daysAhead,
  });

  return useQuery<FindSlotsResponse>({
    queryKey,
    queryFn: () =>
      findSlots({
        postcode: debounced.postcode.trim(),
        duration_minutes: debounced.duration,
        estimated_value_gbp: debounced.value,
        job_ref: args.jobRef,
        days_ahead: daysAhead,
      }),
    enabled,
    staleTime: 60_000, // matches FastAPI's in-memory TTL cache
    gcTime: 5 * 60_000,
    retry: (failureCount, err) => {
      // Auto-retry once on transient upstream errors (502/abort/network).
      if (failureCount >= 1) return false;
      const msg = err instanceof Error ? err.message : String(err);
      return /502|upstream|abort|network/i.test(msg);
    },
    retryDelay: 800,
  });
}

/**
 * Convenience: invalidate every cached slot search. Call after a reschedule
 * (or any other booking write) so the next read re-queries with fresh
 * availability — React Query will refetch any active query, and any
 * inactive query is marked stale so it refetches when next observed.
 */
export function useInvalidateSlots(): () => Promise<void> {
  const qc = useQueryClient();
  return () =>
    qc.invalidateQueries({
      predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "slots",
    });
}
